import React, { useContext, useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import iro from '@jaames/iro/';
import { ColorPicker } from 'react-iro';

import { LeafStateContext } from 'context/state/leaf';

import { useAspenSocket } from 'hooks/useAspenSocket';
import { findSupportedCommand } from 'modules/aspenCommands';
import { getColorProperties } from 'modules/color';

import { LightState } from 'types/aspen/state';

import styles from '../colorPicker.module.scss';

interface TempControlProps {
  initialRender: boolean;
}

export const TempControl = ({ initialRender }: TempControlProps) => {
  const { leaf, dispatch } = useContext(LeafStateContext);
  const leafState = leaf.state as LightState;
  const supportedCommand = findSupportedCommand(leaf.supportedCommands, 'color');
  const [minTemperature, maxTemperature] = supportedCommand?.parameters?.temperatureRange || [1800, 6500];
  const brightnessLevel = leafState.brightness?.level;
  const { sendCommand } = useAspenSocket();

  const { kelvinRGB } = getColorProperties(leafState, brightnessLevel);
  const kelvinRGBIro = useMemo(() => new iro.Color(kelvinRGB), [kelvinRGB]);
  const kelvinRGBString = useMemo(() => kelvinRGBIro.rgbString, [kelvinRGBIro]);

  useEffect(() => {
    if (!initialRender) {
      handleColorChange(kelvinRGBIro);
    }
  }, []);

  const handleColorChange = (color: iro.Color) => {
    const colorPayload = {
      colorType: 'temperature' as const,
      temperature: Math.trunc(color.kelvin),
    };

    dispatch({ type: 'SET_COLOR', payload: colorPayload, origin: { type: leaf.type, trigger: 'user' } }, () =>
      sendCommand({
        leaf,
        command: { command: 'color', parameters: colorPayload },
      }),
    );
  };

  return (
    <Box className={styles.colorPickerContainer}>
      <ColorPicker
        setters={{
          onChangeColor: handleColorChange,
        }}
        options={{
          color: kelvinRGBString,
          width: 300,
          height: 300,
          wheelLightness: false,
          handleRadius: 25,
          activeHandleRadius: 30,
          layoutDirection: 'horizontal',
          layout: [
            {
              component: iro.ui.Slider,
              options: {
                sliderType: 'kelvin',
                sliderShape: 'circle',
                minTemperature,
                maxTemperature,
              },
            },
          ],
        }}
      />
    </Box>
  );
};
